import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { downloadPdf } from './utils/downloadPdf';
import '../../styles/DailyRecordForm.css';

const API_URL = 'https://5uqm251uv5.execute-api.ap-northeast-3.amazonaws.com/prod/api/admin/dailyreport';

const DailyRecordForm = ({ loginCode, date, onSaved, onEvalDate }) => {
  const [record, setRecord] = useState(null);
  const [imageModal, setImageModal] = useState(null);
  const [generatingField, setGeneratingField] = useState(null);

  const formattedDate = format(date, 'yyyy-MM-dd');

  const callApi = async (action, extraBody = {}) => {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, login_code: loginCode, date: formattedDate, ...extraBody })
    });
    return await res.json();
  };

  const parseApiBody = (raw) => {
    if (!raw) return null;
    return typeof raw.body === 'string' ? JSON.parse(raw.body) : raw.body || raw;
  };

  const handleChange = (field, value) => {
    setRecord(prev => ({ ...prev, [field]: value }));
  };

  const toTimeValue = (iso) => iso ? iso.split('T')[1]?.slice(0, 5) : '';
  const toISOTime = (timeStr) => `${formattedDate}T${timeStr}:00`;

  useEffect(() => {
    const fetchRecord = async () => {
      try {
        const raw = await callApi('get-daily-record');
        const data = parseApiBody(raw);
        setRecord(data);

        if (data?.prev_eval_display && onEvalDate) {
          onEvalDate({ date: data.prev_eval_date, display: data.prev_eval_display });
        }
      } catch (err) {
        console.error('[DailyRecordForm] データ取得失敗:', err);
      }
    };

    if (loginCode && date) fetchRecord();
  }, [loginCode, date, onEvalDate]);

  const handleSave = async () => {
    try {
      await callApi('save-daily-record', { record });
      alert('保存しました');
      onSaved?.();
    } catch (e) {
      alert('保存に失敗しました');
    }
  };

  const handleGenerateField = async (field) => {
    setGeneratingField(field);
    try {
      const raw = await callApi(field === 'advice' ? 'generate-advice' : 'generate-support-content');
      const result = parseApiBody(raw);
      if (result?.[field]) {
        setRecord(prev => ({ ...prev, [field]: result[field] }));
      } else {
        alert('生成結果が空でした');
      }
    } catch (err) {
      alert('AI生成に失敗しました');
      console.error(`[generate-${field}]`, err);
    } finally {
      setGeneratingField(null);
    }
  };

  if (!record) return <p>データがありません</p>;

  return (
    <div>
      <table className="excel-style-table">
        <caption><strong>在宅における就労支援記録（評価）</strong></caption>
        <tbody>
          <tr>
            <th>対象者名</th>
            <td>{record.user_name}</td>
            <th>受給者証番号</th>
            <td>
              <input
                type="text"
                value={record.recipient_number || ''}
                onChange={(e) => handleChange('recipient_number', e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <th>実施日</th>
            <td>{format(date, 'yyyy年MM月dd日')}</td>
            <th>実施時間</th>
            <td>
              <input
                type="time"
                value={toTimeValue(record.mark_start)}
                onChange={(e) => handleChange('mark_start', toISOTime(e.target.value))}
                style={{ width: '6em', marginRight: '1em' }}
              />
              ～&emsp;
              <input
                type="time"
                value={toTimeValue(record.mark_end)}
                onChange={(e) => handleChange('mark_end', toISOTime(e.target.value))}
                style={{ width: '6em' }}
              />
            </td>
          </tr>
          <tr>
            <th>支援方法</th>
            <td colSpan={3}>
              <div className="support-method-inline">
                {['訪問', '電話', 'その他'].map((method) => (
                  <label key={method}>
                    <input
                      type="checkbox"
                      checked={record.support_method === method}
                      onChange={() => handleChange('support_method', method)}
                    />
                    {method}
                    {method === 'その他' && (
                      <>
                        （<input
                          type="text"
                          value={record.support_method_note || ''}
                          onChange={(e) => handleChange('support_method_note', e.target.value)}
                          style={{ width: '150px' }}
                        />）
                      </>
                    )}
                  </label>
                ))}
              </div>
            </td>
          </tr>
          {['task_content', 'support_content', 'advice'].map((field) => (
            <tr key={field}>
              <th className="label-cell-with-button">
                <div className="label-cell-content">
                  <div className="label-text">
                    {{
                      task_content: '作業・訓練内容',
                      support_content: '支援内容（1日2回以上）',
                      advice: '対象者の心身の状況及び助言の内容'
                    }[field]}
                  </div>
                  {['support_content', 'advice'].includes(field) && (
                    <button
                      className="custom-button"
                      disabled={generatingField === field}
                      onClick={() => handleGenerateField(field)}
                    >
                      {generatingField === field ? '生成中...' : 'AIでコメント生成'}
                    </button>
                  )}
                </div>
              </th>
              <td colSpan={3}>
                <textarea
                  value={record[field] || ''}
                  onChange={(e) => handleChange(field, e.target.value)}
                />
              </td>
            </tr>
          ))}
          <tr>
            <th>対応・記録者</th>
            <td colSpan={3}>
              <input
                type="text"
                value={record.recorder_name || ''}
                onChange={(e) => handleChange('recorder_name', e.target.value)}
              />
            </td>
          </tr>
        </tbody>
      </table>

      {(record.webcam_photos?.length > 0 || record.screenshots?.length > 0) && (
        <div style={{ marginTop: '2rem' }}>
          <h4>📷 撮影画像一覧（{formattedDate}）</h4>
          {['webcam_photos', 'screenshots'].map(type => (
            record[type]?.length > 0 && (
              <div key={type} style={{ marginTop: '1rem' }}>
                <strong>{type === 'webcam_photos' ? 'Webカメラ写真：' : 'スクリーンショット：'}</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {record[type].map((item, idx) => (
                    <div key={idx} style={{ textAlign: 'center' }}>
                      <img
                        src={item.url}
                        alt={`${type}-${idx}`}
                        width={120}
                        height={90}
                        style={{ objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => setImageModal(item.url)}
                      />
                      <div style={{ fontSize: '0.8rem' }}>{item.time}</div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      <div className="daily-report-save">
        <button className="custom-button" onClick={handleSave}>保存</button>
        <button
          className="custom-button"
          onClick={() =>
            downloadPdf({
              apiUrl: API_URL,
              action: 'get-daily-pdf',
              loginCode,
              date: formattedDate,
              fallbackFilename: 'Daily-Report.pdf'
            })
          }
        >
          PDFダウンロード
        </button>
      </div>

      {imageModal && (
        <div onClick={() => setImageModal(null)} style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000, cursor: 'pointer'
        }}>
          <img src={imageModal} alt="拡大画像" style={{ maxWidth: '90%', maxHeight: '90%' }} />
        </div>
      )}
    </div>
  );
};

export default DailyRecordForm;
